<template>
  <v-navigation-drawer
    v-model="computedValue"
    touchless
    :absolute="false"
    :style="computedPosition"
    :location="location"
    :mobile-breakpoint="0"
    class="elevation-1 pane"
    color="white"
    v-bind="$attrs"
    :width="responsiveWidth"
    scrim="grey-950"
  >
    <!--TABS-->
    <v-toolbar height="0" flat dense color="surface-secondary" class="text-text-primary">
      <template #extension>
        <h3 v-if="hasSingleTab" class="ml-4" data-locator="pane-title">
          {{ tabs[0].title }}
        </h3>

        <v-tabs v-else
                v-model="computedCurrentTab"
                center-active
                :show-arrows="false"
                slider-color="border-inverted"
        >
          <v-tab
            v-for="tab in tabs"
            :value="tab.id"
            :key="tab.id"
            :data-locator="`${tab.id}-tab`"
          >
            <h3>{{tab.title}}</h3>
          </v-tab>
        </v-tabs>
      </template>
    </v-toolbar>

    <AppButton icon
               variant="elevated"
               size="small"
               class="close-button"
               data-locator="close-button"
               @click="onCloseButtonClick"
    >
      <app-material-icon name="close" />
    </AppButton>

    <div class="pane-content">
      <!-- @slot Fixed content above the current tab content -->
      <slot name="fixed-header"></slot>
      <v-window v-model="computedCurrentTab">
        <v-window-item
          v-for="tab in tabs"
          :key="tab.id"
          :value="tab.id"
          :eager="eager"
        >
          <!-- @slot Dynamic slots for the content of the tabs. Starts with "content-" -->
          <slot :name="`content-${tab.id}`"></slot>
        </v-window-item>
      </v-window>
    </div>
  </v-navigation-drawer>
</template>

<script>
import AppButton from '@/common/buttons/AppButton.vue'
// Shared HTML element node used for toggling overflow
let $HTML_ELEMENT

export const PANE_FADE_TRANSITION_TIME = 300

/**
 * Default Pane
 * @displayName Pane
 */
/**
 * A reusable component used for displaying rich contextual information for an item with its associated list still in the viewport
 */
export default {
  name: 'Pane',
  components: { AppButton },
  inheritAttrs: false,
  props: {
    /**
     * @model
     * Determines if the pane is currently opened or not
     */
    value: {
      type: Boolean,
      required: true,
    },
    /**
     * The tab currently opened in the pane. Can be set at any time to change the view
     */
    currentTab: {
      type: [String, Number],
      required: true,
    },
    /**
     * An array of objects with the definition { title: String, id: String } describing a tab entry
     */
    tabs: {
      type: Array,
      required: true,
    },
    /**
     * Determines pane position
     * @default true
     */
    location: {
      type: String,
      default: 'right',
      validator (value) {
        return ['right', 'left'].includes(value)
      },
    },
    /**
     * Apply eager prop to v-tab-item
     * @default false
     */
    eager: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    hasSingleTab () {
      return this.tabs.length === 1
    },
    responsiveWidth () {
      if (this.$vuetify.display.xs) {
        return this.$vuetify.display.width * .8
      }
      if (this.$vuetify.display.mobile) {
        return this.$vuetify.display.width * .5
      } else {
        return this.$vuetify.display.width * .3
      }
    },
    computedValue: {
      get () {
        return this.value
      },
      set (value) {
        this.handleHtmlElementOverflow(value)
        this.$emit('update:modelValue', value)
      },
    },
    computedCurrentTab: {
      get () {
        return this.currentTab
      },
      set (value) {
        this.$emit('update:currentTab', value)
      },
    },
    computedPosition () {
      const position = {
        'z-index': 1100, // needed to be on top of the project navigation drawer
      }

      if (this.location === 'left') {
        position['left'] = 0
      } else {
        position['right'] = 0
      }

      return position
    },
  },
  beforeUnmount () {
    if (!$HTML_ELEMENT) {
      // It will never change. It's the HTML root node afterall
      $HTML_ELEMENT = document.querySelector('html')
    }
    $HTML_ELEMENT.classList.remove('overflow-hidden')
  },
  methods: {
    handleHtmlElementOverflow (value) {
      // If it's the first Pane in this App's lifecycle
      if (!$HTML_ELEMENT) {
        // It will never change. It's the HTML root node afterall
        $HTML_ELEMENT = document.querySelector('html')
      }
      if (value) {
        if (!$HTML_ELEMENT.classList.contains('overflow-hidden')) {
          $HTML_ELEMENT.classList.add('overflow-hidden')
        }
      } else {
        $HTML_ELEMENT.classList.remove('overflow-hidden')
      }
    },
    onCloseButtonClick () {
      /**
       * Close event.
       *
       * @event closeButtonClicked
       */
      this.$emit('closeButtonClicked')
      this.computedValue = false
    },
  },
}
</script>

<style lang="scss" scoped>
.pane-content {
  // 48px is the space used by the toolbar containing the tabs
  // By substracting it we are effectively delimiting the scroll space and
  // making only the content scrollable, thus the toolbar becomes sticky
  height: calc(100% - 48px);
  overflow-y: auto;
  padding: 20px;
}

.close-button {
  position: fixed;
  right: 20px;
  transform: translateY(-50%);
  z-index: 10;
}

:deep(.v-window) {
  overflow: visible;
}

:deep(.v-btn.v-tab) {
  color: rgb(var(--v-theme-text-placeholder));
}
</style>
